/**
 * Resize an image file on the client using a canvas, preserving aspect ratio.
 *
 * Returns a JPEG data URL suitable for sending in JSON payloads. Keeping the
 * result under ~200KB avoids hitting server/body size limits and keeps uploads
 * to S3 fast.
 *
 * @param {File} file - Image file selected by the user.
 * @param {Object} [options]
 * @param {number} [options.maxDimension=512] - Max width/height in px.
 * @param {number} [options.quality=0.85] - JPEG quality (0-1).
 * @returns {Promise<string>} data URL (data:image/jpeg;base64,...)
 */
/** Backdrop painted where a "fit whole image" crop leaves the square empty. */
export const CROP_BACKGROUND = '#0e0e13';

/** Read a File into a data URL. */
export function readFileAsDataURL(file) {
  return new Promise((resolve, reject) => {
    if (!file || !file.type || !file.type.startsWith('image/')) {
      reject(new Error('File is not an image'));
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Failed to read image file'));
    reader.onload = (e) => resolve(e.target.result);
    reader.readAsDataURL(file);
  });
}

/** Decode a data URL / URL into an HTMLImageElement. */
export function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onerror = () => reject(new Error('Failed to decode image'));
    img.onload = () => resolve(img);
    img.src = src;
  });
}

/**
 * Render the square region the user drew in the cropper.
 *
 * The region is in *source image pixels* — the cropper's box, straight from the
 * handles the user dragged. It is allowed to stick out past the image (that is
 * what "fit whole" is: a box bigger than the image), in which case the overhang
 * is filled with a blurred copy rather than dead bars.
 *
 * @param {HTMLImageElement} image - decoded source.
 * @param {Object} box - { x, y, size } in source-image pixels.
 * @param {Object} [options]
 * @param {number} [options.size=512] - output square, in px.
 * @param {number} [options.quality=0.85] - JPEG quality (0-1).
 * @returns {string} data URL (data:image/jpeg;base64,...)
 */
export function renderCropBox(image, box, { size = 512, quality = 0.85 } = {}) {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingQuality = 'high';

  const scale = size / box.size;
  const drawW = image.width * scale;
  const drawH = image.height * scale;
  const x = -box.x * scale;
  const y = -box.y * scale;

  // JPEG has no alpha, so whatever the image doesn't reach has to be painted.
  ctx.fillStyle = CROP_BACKGROUND;
  ctx.fillRect(0, 0, size, size);

  // Flat bars would be baked into the square and then show up again inside
  // every box that draws it — a letterbox on top of a letterbox. Fill the gap
  // with a blurred, zoomed copy of the image instead, so the square is always
  // "full" and the framing still reads as the user's.
  const overhangs = x > 0 || y > 0 || x + drawW < size || y + drawH < size;
  if (overhangs) {
    const cover = size / Math.min(image.width, image.height);
    const coverW = image.width * cover;
    const coverH = image.height * cover;
    ctx.save();
    ctx.filter = `blur(${Math.round(size * 0.05)}px)`;
    // Overdrawn past the edges: a blur samples transparent pixels outside the
    // source rect and would otherwise leave a pale halo around the border.
    ctx.drawImage(
      image,
      (size - coverW * 1.2) / 2,
      (size - coverH * 1.2) / 2,
      coverW * 1.2,
      coverH * 1.2
    );
    ctx.restore();
    // Knock it back so the real image, not the wallpaper, holds the eye.
    ctx.fillStyle = 'rgba(14, 14, 19, 0.55)';
    ctx.fillRect(0, 0, size, size);
  }

  ctx.drawImage(image, x, y, drawW, drawH);

  return canvas.toDataURL('image/jpeg', quality);
}

export function resizeImageToDataURL(file, { maxDimension = 512, quality = 0.85 } = {}) {
  return new Promise((resolve, reject) => {
    if (!file || !file.type || !file.type.startsWith('image/')) {
      reject(new Error('File is not an image'));
      return;
    }

    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Failed to read image file'));
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = () => reject(new Error('Failed to decode image'));
      img.onload = () => {
        try {
          const { width: origW, height: origH } = img;
          const scale = Math.min(1, maxDimension / Math.max(origW, origH));
          const targetW = Math.round(origW * scale);
          const targetH = Math.round(origH * scale);

          const canvas = document.createElement('canvas');
          canvas.width = targetW;
          canvas.height = targetH;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, targetW, targetH);

          // Always normalize to JPEG to keep payload small and predictable.
          const dataUrl = canvas.toDataURL('image/jpeg', quality);
          resolve(dataUrl);
        } catch (err) {
          reject(err);
        }
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}
